import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Box, 
  Typography, 
  FormControlLabel, 
  Checkbox, 
  Alert,
  CircularProgress,
  Collapse,
  Button
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const DynamicESignatureDisclosure = ({ 
  onConsentChange, 
  apiRef, 
  disabled = false 
}) => {
  const [shopInfo, setShopInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disclosureExpanded, setDisclosureExpanded] = useState(false);
  
  // Consent states - SIMPLIFIED TO 2 CHECKBOXES
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(false);
  const [esignatureConsent, setEsignatureConsent] = useState(false);

  // Ã¢Å“â€¦ CRITICAL FIX: Use useRef to track the callback to prevent infinite loops
  const onConsentChangeRef = useRef(onConsentChange);
  onConsentChangeRef.current = onConsentChange;

  // Ã¢Å“â€¦ CRITICAL FIX: Only fetch shop info once when apiRef is available
  useEffect(() => {
    const fetchShopInfo = async () => {
      try {
        setLoading(true);
        // Get shop info from the shop profile endpoint (matches SettingsPage)
        const response = await apiRef.current.fetchWithAuth('/shop/profile/');
        
        // Extract shop info from shop profile response
        setShopInfo({
          business_name: response?.name || 'Your Shop',  // Shop name field
          email: response?.email || 'contact@yourshop.com',
          phone: response?.phone || '(555) 123-4567',
          address: response?.address || '',
          city: response?.city || '',
          state: response?.state || '',
          zip_code: response?.zip_code || ''
        });
      } catch (err) {
        console.error('Error fetching shop info:', err);
        setError('Using default contact information');
        setShopInfo({
          business_name: 'Your Shop',
          email: 'contact@yourshop.com',
          phone: '(555) 123-4567',
          address: 'Your Shop Address'
        });
      } finally {
        setLoading(false);
      }
    };

    if (apiRef?.current) {
      fetchShopInfo();
    } else {
      // Fallback if apiRef is not available
      setLoading(false);
      setShopInfo({
        business_name: 'Your Shop',
        email: 'contact@yourshop.com', 
        phone: '(555) 123-4567',
        address: 'Your Shop Address'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef?.current]); // Ã¢Å“â€¦ Disabled eslint warning for apiRef since it's a mutable ref

  // Ã¢Å“â€¦ CRITICAL FIX: Remove onConsentChange from dependency array to prevent infinite loop
  useEffect(() => {
    const allConsentsGiven = disclosureAcknowledged && esignatureConsent;
    
    if (onConsentChangeRef.current) {
      onConsentChangeRef.current({
        disclosureAcknowledged,
        esignatureConsent,
        allConsentsGiven
      });
    }
  }, [disclosureAcknowledged, esignatureConsent]); // Ã¢Å“â€¦ Removed onConsentChange from deps

  // Generate dynamic disclosure text
  const generateDisclosureText = (shop) => {
    const shopName = shop?.business_name || shop?.name || 'Your Shop';
    const shopEmail = shop?.email || 'contact@yourshop.com';
    const shopPhone = shop?.phone || '(555) 123-4567';
    const shopAddress = shop?.address ? 
      `${shop.address}${shop.city ? ', ' + shop.city : ''}${shop.state ? ', ' + shop.state : ''}${shop.zip_code ? ' ' + shop.zip_code : ''}` : 
      'Your shop address on file';

    return `LEGAL EFFECT: Electronic signatures have the same legal force as handwritten signatures.

CONSENT: You consent to receive and sign this document electronically.

PAPER COPIES: Contact ${shopName} at ${shopPhone} or ${shopEmail} to request paper copies.

REQUIREMENTS: Internet access, current web browser, ability to view PDFs.

RECORDS ACCESS: Contact ${shopName} for electronic record copies.

WITHDRAWAL: You may withdraw consent at any time by contacting ${shopName}.

CONTACT: ${shopName} | ${shopPhone} | ${shopEmail} | ${shopAddress}

Updated: ${new Date().toLocaleDateString()}`;
  };

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mt: 1, p: 2, textAlign: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
          Loading disclosure...
        </Typography>
      </Card>
    );
  }

  const allRequirementsAccepted = disclosureAcknowledged && esignatureConsent;

  return (
    <Card variant="outlined" sx={{ mt: 1, p: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'warning.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1, color: 'warning.main', fontSize: '1.2rem' }} />
          <Typography variant="subtitle2" sx={{ color: 'warning.main', fontSize: '0.9rem' }}>
            Electronic Signature Disclosure
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => setDisclosureExpanded(!disclosureExpanded)}
          endIcon={disclosureExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.75rem' }}
        >
          Read
        </Button>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>
        Federal law requires disclosure about electronic signatures before use.
      </Typography>
      
      {error && (
        <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            {error}
          </Typography>
        </Alert>
      )}
      
      <Collapse in={disclosureExpanded}>
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 1, 
          backgroundColor: 'action.hover', 
          maxHeight: '200px', 
          overflow: 'auto' 
        }}>
          <Typography variant="body2" sx={{ 
            whiteSpace: 'pre-line', 
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            lineHeight: 1.3
          }}>
            {generateDisclosureText(shopInfo)}
          </Typography>
        </Box>
      </Collapse>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={disclosureAcknowledged}
              onChange={(e) => setDisclosureAcknowledged(e.target.checked)}
              color="primary"
              disabled={disabled}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              I have read the electronic signature disclosure
            </Typography>
          }
          sx={{ 
            ml: 0,
            alignItems: 'center'
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={esignatureConsent}
              onChange={(e) => setEsignatureConsent(e.target.checked)}
              color="primary"
              disabled={disabled}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              I consent to electronic signatures for this document
            </Typography>
          }
          sx={{ 
            ml: 0,
            alignItems: 'center'
          }}
        />
      </Box>
      
      {allRequirementsAccepted && (
        <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            Electronic signature disclosure completed.
          </Typography>
        </Alert>
      )}
    </Card>
  );
};

export default DynamicESignatureDisclosure;